/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Typography, Box } from '@mui/material';
import type { i18n, TFunction } from 'i18next';
import React from 'react';
import { AlertIcon, TooltipSelect } from '@opengeoweb/shared';
import type { LayerProps } from '@opengeoweb/webmap';
import { useCoreTranslation } from '../../../../../utils/i18n';

export const showNoLayers = (
  t: TFunction<'core', i18n>,
): React.ReactElement => {
  return (
    <Box
      sx={{
        paddingLeft: 1,
        height: '32px',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          fontSize: (theme): number | string =>
            theme.palette.geowebColors.layerManager.tableRowDefaultText
              .fontSize!,
          color: 'geowebColors.layerManager.tableRowDefaultText.rgba',
          fontWeight: 500,
        }}
        variant="body1"
      >
        {t('layermanager-baselayers-no-available')}
      </Typography>
    </Box>
  );
};

export interface RenderLayersProps {
  onChangeLayerName: (name: string) => void;
  layerName: string;
  layers: LayerProps[];
  isEnabled?: boolean;
  tooltipPrefix?: string;
  skipLocalStyling?: boolean;
}

const getRenderLayersValues = (
  layerName: string,
  layers: LayerProps[],
): {
  currentValue: string;
  currentIndex: number;
  extendedLayers: LayerProps[];
} => {
  const serviceLayer = layers.find((layer) => layer.name === layerName);
  if (!serviceLayer && layerName) {
    return {
      currentValue: layerName,
      currentIndex: layers.length,
      extendedLayers: [
        ...layers,
        {
          name: layerName,
          title: layerName,
          leaf: true,
          path: [],
        },
      ],
    };
  }
  return {
    currentValue: serviceLayer?.name || '',
    currentIndex: layers.findIndex(
      (layer) => layer.name === serviceLayer?.name,
    ),
    extendedLayers: layers,
  };
};

const RenderLayers: React.FC<RenderLayersProps> = ({
  onChangeLayerName,
  layerName,
  layers = [],
  isEnabled = true,
  tooltipPrefix,
  skipLocalStyling,
}: RenderLayersProps) => {
  const { t } = useCoreTranslation();
  const nonEmptyTooltipPrefix = tooltipPrefix ?? `${t('layermanager-layer')}: `;

  if (!layers?.length) {
    return showNoLayers(t);
  }

  const selectLayer = (event: SelectChangeEvent<unknown>): void => {
    event.stopPropagation();
    onChangeLayerName(event.target.value as string);
  };

  const { currentIndex, currentValue, extendedLayers } = getRenderLayersValues(
    layerName,
    layers,
  );

  const list = extendedLayers.map((layer) => ({
    value: layer.name!,
  }));

  return (
    <TooltipSelect
      currentIndex={currentIndex}
      disableUnderline
      inputProps={{
        SelectDisplayProps: {
          'data-testid': 'selectLayer',
        },
      }}
      isEnabled={isEnabled}
      list={list}
      onChange={selectLayer}
      onChangeMouseWheel={(e): void => onChangeLayerName(e.value)}
      // Omit possible warning sign in the selected item due to layout effects
      renderValue={(value): string =>
        extendedLayers.find((layer): boolean => layer.name === value)!.title!
      }
      skipLocalStyling={skipLocalStyling}
      tooltip={`${nonEmptyTooltipPrefix}${extendedLayers[currentIndex]?.title}`}
      value={currentValue}
    >
      <MenuItem disabled>{t('layermanager-layer')}</MenuItem>
      {extendedLayers.map(
        (layerFromServiceLayers: LayerProps, currentIndex) => (
          <MenuItem
            disabled={!layerFromServiceLayers.leaf!} // show categories as disabled
            key={layerFromServiceLayers.name}
            sx={{ '& .MuiSvgIcon-root': { marginLeft: '28px' } }}
            value={layerFromServiceLayers.name!}
          >
            {layerFromServiceLayers.title}
            {currentIndex === layers.length && <AlertIcon severity="error" />}
          </MenuItem>
        ),
      )}
    </TooltipSelect>
  );
};

export default RenderLayers;
